/**
 * Project Detail → Site tab. 1:1 with the design folder's
 * `project-details.html#ptab-site`.
 *
 * One DPR (Daily Progress Report) per project per calendar day,
 * stored at `dpr/{projectId}_{date}`. Mobile + web read/write the
 * EXACT same doc (`lib/dpr.ts`).
 *
 * Layout (matches design folder exactly):
 *   Card header — "Daily Progress Report (DPR) - {date}" + date
 *     prev/next/picker + Download PDF (stub for v1)
 *   responsive-grid-2-1 body:
 *     LEFT
 *       - Today's Summary (workDone) + Site Status (weather + on-track)
 *       - Work Logs table (placeholder; pulls from tasks in v2)
 *       - Tomorrow's Plan (tomorrowPlan, multiline → bullet rendering)
 *       - Site Issues / Blockers (issues, red banner)
 *     RIGHT
 *       - Site Attendances (placeholder; pulls from attendance in v2)
 *       - Material Requests (placeholder; v2)
 *       - Site Photos (photoUris thumbnails + add-photo stub)
 *
 * View / Edit modes:
 *   Default = view (renders whatever the latest snapshot contains
 *   for the picked date, or an empty state).
 *   "Edit DPR" → swaps every editable section to inputs + a sticky
 *   Save / Cancel footer. Save writes via `upsertDpr` with the same
 *   field set as mobile.
 *
 * Out of scope for v1 (clearly marked in the UI):
 *   - Attendances (`useAttendance` not yet ported)
 *   - Material requests (no port yet)
 *   - Work-logs row computed from tasks (`useTasks` not yet ported)
 *   - R2 photo upload (R2 callable exists; web helper not built;
 *     photo URLs read & rendered, but Add Photo is a notice stub)
 *   - PDF export
 */
import { useEffect, useMemo, useRef, useState } from 'react';

import { canWriteDpr } from '@shared/perms';

import { useAuth } from '@/auth/AuthProvider';
import {
  hoursFor,
  payableUnitFor,
  type AttendanceRecord,
  type AttendanceStatus,
} from '@/lib/attendance';
import type { MaterialRequest } from '@/lib/materialRequests';
import {
  WEATHER_OPTIONS,
  formatDateKey,
  formatWeekdayKey,
  shiftDateKey,
  todayLocalDateKey,
  upsertDpr,
  type Weather,
} from '@/lib/dpr';
import { FILE_CATEGORIES } from '@/lib/designs';
import {
  buildSiteReport,
  buildSiteReportHtml,
  openHtmlForPrint,
  type SiteReportInputAttendance,
  type SiteReportInputMaterialRequest,
  type SiteReportInputWorkLog,
} from '@/lib/projectReports';
import { uploadFileToR2 } from '@/lib/r2Upload';
import { useCurrentOrgRole } from '@/lib/useCurrentOrgRole';
import { useDesigns } from '@/lib/useDesigns';
import { useDpr } from '@/lib/useDpr';
import { useMaterialRequests } from '@/lib/useMaterialRequests';
import { useProjectAttendance } from '@/lib/useProjectAttendance';
import { useProjectTasks } from '@/lib/useProjectTasks';
import {
  relativeTime,
  useProjectTaskUpdatesForDate,
  type ProjectTaskUpdateRow,
} from '@/lib/useProjectTaskUpdatesForDate';
import type { ProjectListItem } from '@/lib/useProjects';

// ───── Inline styles (consistent with CreateLead form) ─────

const INPUT: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid var(--border)',
  borderRadius: 6,
  background: '#fff',
  fontSize: 13,
  color: 'var(--text-primary)',
  fontFamily: 'inherit',
};

const LABEL: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  color: 'var(--text-tertiary)',
  textTransform: 'uppercase',
  letterSpacing: '0.4px',
  marginBottom: 8,
};

const SUBCARD: React.CSSProperties = {
  border: '1px solid var(--border)',
  borderRadius: 8,
  padding: 16,
};

// ───── Component ─────

export function SiteTab({ project }: { project: ProjectListItem }) {
  const { user } = useAuth();
  const orgId = project.orgId ?? null;
  const { role: orgRole } = useCurrentOrgRole();
  const canWrite = canWriteDpr(orgRole);

  // Selected date key (YYYY-MM-DD). Default = today.
  const [dateKey, setDateKey] = useState<string>(() => todayLocalDateKey());
  const { data: dpr, loading } = useDpr(project.id, dateKey);

  // Project tasks + day-bucketed work-log updates for the same date.
  // The updates feed renders in the "Work Logs" section, replacing
  // the v1 placeholder. Mobile + web write the same docs at
  // `tasks/{taskId}/updates`, so an update posted on either client
  // shows up on both within ~1s.
  const { tasks: projectTasks } = useProjectTasks(orgId, project.id);
  const taskRefs = useMemo(
    () => projectTasks.map((t) => ({ id: t.id, title: t.title })),
    [projectTasks],
  );
  const { data: dayUpdates, loading: updatesLoading } =
    useProjectTaskUpdatesForDate(project.id, dateKey, taskRefs);

  // Live attendance for the selected date — drives the right-rail
  // Site Attendances section + the report's attendance table.
  const { data: attendanceRows, summary: attendanceSummary } =
    useProjectAttendance(orgId, project.id, dateKey);

  // All project material requests; we filter to today's date below.
  const { data: allMaterialRequests } = useMaterialRequests(
    orgId,
    project.id,
  );
  const requestsToday = useMemo(() => {
    return allMaterialRequests.filter((r) => {
      // Skip rejected — mobile parity (mobile's SiteTab excludes
      // rejected so they don't pollute the day-of-action view).
      if (r.status === 'rejected') return false;
      if (!r.createdAt) return false;
      const d = new Date(r.createdAt.toMillis());
      // Format YYYY-MM-DD in local time to match `dateKey`.
      const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      return k === dateKey;
    });
  }, [allMaterialRequests, dateKey]);

  // Live files list — drives the "Project files" overview in the
  // right rail (just a count + per-category breakdown).
  const { data: files } = useDesigns(project.id, orgId ?? undefined);
  const filesByCategory = useMemo(() => {
    const m: Record<string, number> = {};
    for (const c of FILE_CATEGORIES) m[c.key] = 0;
    for (const f of files) {
      const k = (f.category ?? 'other') as string;
      m[k] = (m[k] ?? 0) + 1;
    }
    return m;
  }, [files]);

  // Edit-mode form state. Initialised from the snapshot when entering
  // edit mode (or when the snapshot updates and we're not mid-edit).
  const [editing, setEditing] = useState(false);
  const [workDone, setWorkDone] = useState('');
  const [weather, setWeather] = useState<Weather>('clear');
  const [weatherNote, setWeatherNote] = useState('');
  const [issues, setIssues] = useState('');
  const [tomorrowPlan, setTomorrowPlan] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // R2 photo upload state — Site Photos section's add-photo flow.
  // Multiple uploads in flight at once are allowed; we just track an
  // `uploading` flag for the button's visual state.
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInputRef = useRef<HTMLInputElement | null>(null);

  // Report download — guards against double-clicks while the report
  // tab is opening + auto-printing.
  const [reportBusy, setReportBusy] = useState(false);

  // Hydrate form whenever the snapshot resolves AND we're not mid-edit.
  useEffect(() => {
    if (editing) return;
    setWorkDone(dpr?.workDone ?? '');
    setWeather((dpr?.weather as Weather) ?? 'clear');
    setWeatherNote(dpr?.weatherNote ?? '');
    setIssues(dpr?.issues ?? '');
    setTomorrowPlan(dpr?.tomorrowPlan ?? '');
  }, [dpr, editing, dateKey]);

  const isToday = dateKey === todayLocalDateKey();

  // Save → upsertDpr with the v1 fields. We preserve mobile-managed
  // snapshot fields (staffPresent, staffTotal, material*) by passing
  // through whatever the snapshot last held (or zeros for new docs).
  const onSave = async () => {
    if (!orgId || !user) {
      setError('Sign-in required.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await upsertDpr({
        orgId,
        projectId: project.id,
        date: dateKey,
        workDone: workDone.trim(),
        weather,
        weatherNote: weatherNote.trim(),
        issues: issues.trim(),
        tomorrowPlan: tomorrowPlan.trim(),
        photoUris: dpr?.photoUris ?? [],
        // Preserve mobile-written snapshot fields untouched.
        staffPresent: dpr?.staffPresent ?? 0,
        staffTotal: dpr?.staffTotal ?? 0,
        materialRequestedCount: dpr?.materialRequestedCount ?? 0,
        materialRequestedValue: dpr?.materialRequestedValue ?? 0,
        materialReceivedCount: dpr?.materialReceivedCount ?? 0,
        materialUsedCount: dpr?.materialUsedCount ?? 0,
        createdBy: dpr?.createdBy ?? user.uid,
        isUpdate: Boolean(dpr),
      });
      setEditing(false);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const onDiscard = () => {
    setEditing(false);
    // Form re-hydrates from snapshot on the next render.
  };

  // ── Photo upload (R2 `kind: 'dpr'`) ────────────────────────────
  //
  // Picking a file → upload to R2 → append the returned `publicUrl`
  // to `dpr.photoUris[]` via `upsertDpr`. The doc may not exist yet
  // for this date — upsertDpr handles create vs update.
  async function handlePickPhoto(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.currentTarget.files?.[0];
    e.currentTarget.value = ''; // allow re-picking same file
    if (!file) return;
    if (!orgId || !user) {
      setError('Sign-in required.');
      return;
    }
    setError(null);
    setUploadingPhoto(true);
    try {
      // refId = the DPR's deterministic doc id (`${projectId}_${date}`)
      // so multiple photos on the same day group sensibly in R2.
      const refId = `${project.id}_${dateKey}`;
      const { publicUrl } = await uploadFileToR2({
        file,
        kind: 'dpr',
        refId,
        orgId,
      });
      const existing = dpr?.photoUris ?? [];
      await upsertDpr({
        orgId,
        projectId: project.id,
        date: dateKey,
        workDone: dpr?.workDone ?? '',
        weather: (dpr?.weather as Weather) ?? 'clear',
        weatherNote: dpr?.weatherNote ?? '',
        issues: dpr?.issues ?? '',
        tomorrowPlan: dpr?.tomorrowPlan ?? '',
        photoUris: [...existing, publicUrl],
        staffPresent: dpr?.staffPresent ?? 0,
        staffTotal: dpr?.staffTotal ?? 0,
        materialRequestedCount: dpr?.materialRequestedCount ?? 0,
        materialRequestedValue: dpr?.materialRequestedValue ?? 0,
        materialReceivedCount: dpr?.materialReceivedCount ?? 0,
        materialUsedCount: dpr?.materialUsedCount ?? 0,
        createdBy: dpr?.createdBy ?? user.uid,
        isUpdate: Boolean(dpr),
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.warn('[site] photo upload failed:', err);
      setError(`Photo upload failed: ${msg}`);
    } finally {
      setUploadingPhoto(false);
    }
  }

  // ── Site report download ───────────────────────────────────────
  //
  // Joins live data — DPR + attendance + same-day material requests
  // + work logs + photos + files repository — into a comprehensive
  // HTML report and opens it in a new tab with the print dialog
  // pre-triggered (so the user can Save-as-PDF).
  function handleDownloadReport() {
    if (reportBusy) return;
    setReportBusy(true);
    try {
      const reportDate = (() => {
        const [y, m, d] = dateKey.split('-').map(Number);
        return new Date(y, (m ?? 1) - 1, d ?? 1);
      })();

      // Attendance rows shaped for the report template.
      const attendanceForReport: SiteReportInputAttendance[] = attendanceRows.map(
        (r) => ({
          labourName: r.labourName,
          status: r.status,
          hours: hoursFor(r.status),
        }),
      );

      // Material requests shaped for the report template.
      const materialsForReport: SiteReportInputMaterialRequest[] =
        requestsToday.map((r) => ({
          id: r.id,
          title:
            r.title?.trim() ||
            (r.items?.[0]?.name
              ? `${r.items[0].name}${r.items.length > 1 ? ` + ${r.items.length - 1} more` : ''}`
              : `Request ${r.id.slice(-6)}`),
          itemCount: r.items?.length ?? 0,
          status: r.status,
          totalValue: r.totalValue ?? 0,
          // MaterialRequest only stores `createdBy` (uid). A future
          // turn could join with org members to surface the display
          // name; for v1 the report omits the requester.
        }));

      // Work logs shaped for the report template — compute the
      // previous-progress delta per row (same algorithm the WorkLogRow
      // component uses inline). Photo URLs flow through so the report
      // can render inline thumbnails AND aggregate them into the
      // Site Photos grid.
      const workLogsForReport: SiteReportInputWorkLog[] = dayUpdates.map(
        (u, i) => ({
          taskTitle: u.taskTitle,
          authorName: u.authorName,
          progress: u.progress,
          deltaPct: u.progress - previousProgressForUpdate(u, dayUpdates, i),
          text: u.text,
          photoUris: u.photoUris,
        }),
      );

      const data = buildSiteReport({
        project,
        reportDate,
        weekdayLabel: formatWeekdayKey(dateKey),
        dpr: dpr ?? null,
        attendance: attendanceForReport,
        attendanceSummary: {
          present: attendanceSummary.present,
          halfDay: attendanceSummary.halfDay,
          total: attendanceSummary.total,
        },
        materialRequests: materialsForReport,
        workLogs: workLogsForReport,
        files: {
          total: files.length,
          byCategory: filesByCategory,
        },
      });

      // Suppress unused-var lint for payableUnitFor — it's exported from
      // attendance.ts for clarity but the report uses
      // attendanceSummary.halfDay * 0.5 inline. Reference here so the
      // import stays useful if we ever add per-worker payable units.
      void payableUnitFor;

      const html = buildSiteReportHtml(data);
      openHtmlForPrint(
        html,
        `${data.projectName} — Site Report ${data.reportDate}`,
      );
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      console.warn('[site] report build failed:', e);
      setError(`Report failed: ${msg}`);
    } finally {
      // Reset quickly so the user can re-trigger after the new tab
      // opens (e.g., they accidentally closed it).
      setTimeout(() => setReportBusy(false), 600);
    }
  }

  // Pre-rendered bullet items for the read-only Tomorrow's Plan box.
  const tomorrowBullets = useMemo(
    () =>
      (dpr?.tomorrowPlan ?? '')
        .split(/\r?\n/)
        .map((s) => s.trim())
        .filter(Boolean),
    [dpr?.tomorrowPlan],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {error ? (
        <div
          style={{
            background: 'var(--critical-bg)',
            color: 'var(--critical-text)',
            padding: 12,
            borderRadius: 6,
            fontSize: 13,
          }}
        >
          {error}
        </div>
      ) : null}

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {/* Header */}
        <div
          className="card-header"
          style={{
            justifyContent: 'space-between',
            padding: '16px 20px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 12,
          }}
        >
          <div
            className="card-title"
            style={{ display: 'flex', alignItems: 'center', gap: 8 }}
          >
            Daily Progress Report (DPR) —{' '}
            <span style={{ color: 'var(--text-primary)' }}>
              {formatDateKey(dateKey)}
            </span>
            <span
              style={{
                fontSize: 11,
                color: 'var(--text-tertiary)',
                fontWeight: 500,
              }}
            >
              {formatWeekdayKey(dateKey)}
              {isToday ? ' · Today' : ''}
            </span>
          </div>

          <div
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            {/* Date pager */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                border: '1px solid var(--border)',
                borderRadius: 6,
                background: '#fff',
                overflow: 'hidden',
              }}
            >
              <button
                type="button"
                className="btn-ghost"
                onClick={() => setDateKey((k) => shiftDateKey(k, -1))}
                aria-label="Previous day"
                title="Previous day"
                style={{
                  padding: '6px 10px',
                  border: 'none',
                  borderRight: '1px solid var(--border)',
                }}
              >
                <i className="ph-bold ph-caret-left" />
              </button>
              <input
                type="date"
                value={dateKey}
                onChange={(e) =>
                  setDateKey(e.currentTarget.value || todayLocalDateKey())
                }
                style={{
                  border: 'none',
                  padding: '6px 8px',
                  fontSize: 12,
                  background: 'transparent',
                  color: 'var(--text-primary)',
                  fontFamily: 'inherit',
                }}
              />
              <button
                type="button"
                className="btn-ghost"
                onClick={() => setDateKey((k) => shiftDateKey(k, 1))}
                aria-label="Next day"
                title="Next day"
                style={{
                  padding: '6px 10px',
                  border: 'none',
                  borderLeft: '1px solid var(--border)',
                }}
                disabled={dateKey >= todayLocalDateKey()}
              >
                <i className="ph-bold ph-caret-right" />
              </button>
            </div>

            {!isToday ? (
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setDateKey(todayLocalDateKey())}
                style={{ height: 32, fontSize: 12 }}
              >
                Today
              </button>
            ) : null}

            <button
              type="button"
              className="btn-secondary"
              style={{ height: 32, fontSize: 12 }}
              onClick={handleDownloadReport}
              disabled={reportBusy}
              title="Generate a printable Site Report for this date"
            >
              <i className="ph ph-download-simple" />{' '}
              {reportBusy ? 'Preparing…' : 'Download Report'}
            </button>

            {!editing ? (
              canWrite ? (
                // Hidden for roles that can't write — Firestore rule blocks it anyway
                <button
                  type="button"
                  className="btn-primary"
                  style={{ height: 32, fontSize: 12 }}
                  onClick={() => setEditing(true)}
                >
                  <i className="ph ph-pencil-simple" />{' '}
                  {dpr ? 'Edit DPR' : 'Create DPR'}
                </button>
              ) : null
            ) : (
              <>
                <button
                  type="button"
                  className="btn-secondary"
                  style={{ height: 32, fontSize: 12 }}
                  onClick={onDiscard}
                  disabled={busy}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  style={{ height: 32, fontSize: 12 }}
                  onClick={onSave}
                  disabled={busy}
                >
                  {busy ? 'Saving…' : 'Save DPR'}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Body */}
        {loading ? (
          <div
            style={{
              padding: 48,
              textAlign: 'center',
              color: 'var(--text-secondary)',
            }}
          >
            Loading DPR…
          </div>
        ) : (
          <div className="responsive-grid-2-1" style={{ padding: 20 }}>
            {/* ─── LEFT COLUMN ─── */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 20,
              }}
            >
              {/* Today's Summary + Site Status */}
              <div
                style={{
                  display: 'flex',
                  gap: 20,
                  flexWrap: 'wrap',
                }}
              >
                <div style={{ ...SUBCARD, flex: 1, minWidth: 240 }}>
                  <div style={LABEL}>Today's Summary</div>
                  {editing ? (
                    <textarea
                      style={{
                        ...INPUT,
                        minHeight: 90,
                        resize: 'vertical',
                        fontSize: 13,
                      }}
                      placeholder="What did the team complete today?"
                      value={workDone}
                      onChange={(e) => setWorkDone(e.currentTarget.value)}
                    />
                  ) : (
                    <p
                      style={{
                        fontSize: 13,
                        color: workDone
                          ? 'var(--text-secondary)'
                          : 'var(--text-tertiary)',
                        lineHeight: 1.5,
                        margin: 0,
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {workDone || 'No summary saved for this day.'}
                    </p>
                  )}
                </div>

                <div
                  style={{
                    ...SUBCARD,
                    width: 220,
                    background: '#FAFAFA',
                  }}
                >
                  <div style={LABEL}>Site Status</div>
                  <div
                    style={{
                      fontSize: 16,
                      fontWeight: 600,
                      color: issues
                        ? 'var(--critical)'
                        : workDone
                          ? 'var(--success)'
                          : 'var(--text-tertiary)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    {issues ? (
                      <>
                        <i className="ph-fill ph-warning-circle" /> Blocked
                      </>
                    ) : workDone ? (
                      <>
                        <i className="ph-fill ph-check-circle" /> On Track
                      </>
                    ) : (
                      <>
                        <i className="ph ph-circle" /> No log
                      </>
                    )}
                  </div>
                  {editing ? (
                    <div
                      style={{
                        marginTop: 10,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 6,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 11,
                          color: 'var(--text-tertiary)',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '0.4px',
                        }}
                      >
                        Weather
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          gap: 4,
                          flexWrap: 'wrap',
                        }}
                      >
                        {WEATHER_OPTIONS.map((w) => (
                          <button
                            key={w.key}
                            type="button"
                            onClick={() => setWeather(w.key)}
                            title={w.label}
                            style={{
                              padding: '4px 8px',
                              border:
                                weather === w.key
                                  ? '1.5px solid var(--brand)'
                                  : '1px solid var(--border)',
                              background:
                                weather === w.key ? 'var(--brand-bg)' : '#fff',
                              color:
                                weather === w.key
                                  ? 'var(--brand)'
                                  : 'var(--text-secondary)',
                              borderRadius: 4,
                              fontSize: 11,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4,
                            }}
                          >
                            <i className={w.icon} />
                            {w.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div
                      style={{
                        fontSize: 12,
                        color: 'var(--text-secondary)',
                        marginTop: 4,
                      }}
                    >
                      Weather:{' '}
                      {WEATHER_OPTIONS.find((w) => w.key === weather)?.label ??
                        '—'}
                      {weatherNote ? ` · ${weatherNote}` : ''}
                    </div>
                  )}
                </div>
              </div>

              {/* Work Logs — live feed of task updates posted on
                  this date. Mobile + web write to the same docs at
                  `tasks/{taskId}/updates`. */}
              <div
                style={{
                  border: '1px solid var(--border)',
                  borderRadius: 8,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    background: '#F9FAFB',
                    padding: '12px 16px',
                    borderBottom: '1px solid var(--border)',
                    fontSize: 13,
                    fontWeight: 600,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <span>Work Logs</span>
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 400,
                      color: 'var(--text-secondary)',
                    }}
                  >
                    {dayUpdates.length}{' '}
                    {dayUpdates.length === 1 ? 'update' : 'updates'}
                  </span>
                </div>
                {updatesLoading ? (
                  <div
                    style={{
                      padding: 24,
                      textAlign: 'center',
                      fontSize: 13,
                      color: 'var(--text-tertiary)',
                    }}
                  >
                    Loading work logs…
                  </div>
                ) : dayUpdates.length === 0 ? (
                  <div
                    style={{
                      padding: 24,
                      textAlign: 'center',
                      fontSize: 13,
                      color: 'var(--text-tertiary)',
                    }}
                  >
                    No work logs posted on this day yet. Post one from the
                    Timeline tab's "Log work" button on any task row.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {dayUpdates.map((row, i) => (
                      <WorkLogRow
                        key={row.id}
                        row={row}
                        previousProgress={previousProgressForUpdate(
                          row,
                          dayUpdates,
                          i,
                        )}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Tomorrow's Plan */}
              <div style={SUBCARD}>
                <div style={LABEL}>Tomorrow's Plan</div>
                {editing ? (
                  <textarea
                    style={{
                      ...INPUT,
                      minHeight: 90,
                      resize: 'vertical',
                      fontSize: 13,
                    }}
                    placeholder="One plan item per line — they'll render as bullets."
                    value={tomorrowPlan}
                    onChange={(e) => setTomorrowPlan(e.currentTarget.value)}
                  />
                ) : tomorrowBullets.length === 0 ? (
                  <p
                    style={{
                      fontSize: 13,
                      color: 'var(--text-tertiary)',
                      margin: 0,
                    }}
                  >
                    No plan recorded for tomorrow.
                  </p>
                ) : (
                  <ul
                    style={{
                      margin: 0,
                      paddingLeft: 20,
                      fontSize: 13,
                      color: 'var(--text-secondary)',
                      lineHeight: 1.6,
                    }}
                  >
                    {tomorrowBullets.map((b, i) => (
                      <li key={i}>{b}</li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Site Issues / Blockers */}
              <div
                style={{
                  border: '1px solid #FECACA',
                  background: '#FEF2F2',
                  borderRadius: 8,
                  padding: 16,
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: 'var(--critical)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.4px',
                    marginBottom: 8,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <i className="ph-bold ph-warning-circle" /> Site Issues /
                  Blockers
                </div>
                {editing ? (
                  <textarea
                    style={{
                      ...INPUT,
                      minHeight: 70,
                      resize: 'vertical',
                      borderColor: '#FECACA',
                      background: '#fff',
                    }}
                    placeholder="Any blockers, restrictions, or alerts?"
                    value={issues}
                    onChange={(e) => setIssues(e.currentTarget.value)}
                  />
                ) : (
                  <p
                    style={{
                      fontSize: 13,
                      color: issues ? '#991B1B' : 'var(--text-tertiary)',
                      lineHeight: 1.5,
                      margin: 0,
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {issues || 'No blockers reported today.'}
                  </p>
                )}
              </div>
            </div>

            {/* ─── RIGHT COLUMN ─── */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 20,
              }}
            >
              {/* Site Attendances — live attendance rows for this
                  date. The right-rail summary shows the bucket
                  counts (P / H / A / L / W) so the studio can scan
                  the day at a glance. */}
              <RightSection
                title="Site Attendances"
                right={
                  <span style={{ color: 'var(--text-secondary)' }}>
                    {attendanceSummary.total > 0
                      ? `${attendanceSummary.present}P · ${attendanceSummary.halfDay}H · ${attendanceSummary.absent}A`
                      : 'No records'}
                  </span>
                }
              >
                {attendanceRows.length === 0 ? (
                  <div
                    style={{
                      padding: 16,
                      fontSize: 12,
                      color: 'var(--text-tertiary)',
                      textAlign: 'center',
                    }}
                  >
                    No attendance marked for {formatDateKey(dateKey)}. Mark
                    workers from the Attendances tab.
                  </div>
                ) : (
                  <div>
                    {attendanceRows.map((r) => (
                      <AttendanceLine key={r.id} row={r} />
                    ))}
                  </div>
                )}
              </RightSection>

              {/* Material Requests — same-day requests, excluding
                  rejected (mobile parity). */}
              <RightSection
                title="Material Requests"
                right={
                  requestsToday.length > 0 ? (
                    <span style={{ color: 'var(--text-secondary)' }}>
                      {requestsToday.length}{' '}
                      {requestsToday.length === 1 ? 'request' : 'requests'} ·
                      ₹
                      {requestsToday
                        .reduce((s, r) => s + (r.totalValue ?? 0), 0)
                        .toLocaleString('en-IN')}
                    </span>
                  ) : null
                }
              >
                {requestsToday.length === 0 ? (
                  <div
                    style={{
                      padding: 16,
                      fontSize: 12,
                      color: 'var(--text-tertiary)',
                      textAlign: 'center',
                    }}
                  >
                    No material requests raised on {formatDateKey(dateKey)}.
                  </div>
                ) : (
                  <div>
                    {requestsToday.map((r) => (
                      <MaterialRequestLine key={r.id} req={r} />
                    ))}
                  </div>
                )}
              </RightSection>

              {/* Project Files — total count + per-category chips
                  (whole-project, not date-scoped — file uploads
                  aren't typically a "today" thing the way attendance
                  + material requests are). */}
              <RightSection
                title="Project Files"
                right={
                  <span style={{ color: 'var(--text-secondary)' }}>
                    {files.length}{' '}
                    {files.length === 1 ? 'file' : 'files'}
                  </span>
                }
              >
                {files.length === 0 ? (
                  <div
                    style={{
                      padding: 16,
                      fontSize: 12,
                      color: 'var(--text-tertiary)',
                      textAlign: 'center',
                    }}
                  >
                    No files uploaded. Drop layouts, PDFs, MOMs in the
                    Files tab.
                  </div>
                ) : (
                  <div
                    style={{
                      padding: 12,
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 6,
                    }}
                  >
                    {FILE_CATEGORIES.map((c) => {
                      const n = filesByCategory[c.key] ?? 0;
                      if (n === 0) return null;
                      return (
                        <span
                          key={c.key}
                          style={{
                            padding: '3px 9px',
                            fontSize: 10,
                            fontWeight: 700,
                            background: '#F3F4F6',
                            color: 'var(--text-secondary)',
                            borderRadius: 999,
                            letterSpacing: 0.4,
                            textTransform: 'uppercase',
                          }}
                        >
                          {c.label} · {n}
                        </span>
                      );
                    })}
                  </div>
                )}
              </RightSection>

              {/* Site Photos */}
              <div style={SUBCARD}>
                <div style={LABEL}>Site Photos</div>
                <div
                  style={{
                    display: 'flex',
                    gap: 12,
                    flexWrap: 'wrap',
                  }}
                >
                  {(dpr?.photoUris ?? []).map((url) => (
                    <a
                      key={url}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        width: 70,
                        height: 70,
                        borderRadius: 6,
                        overflow: 'hidden',
                        background: '#E5E7EB',
                        display: 'block',
                      }}
                    >
                      <img
                        src={url}
                        alt="Site"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block',
                        }}
                      />
                    </a>
                  ))}
                  {canWrite ? (
                    // Hidden for roles that can't write — Firestore rule blocks it anyway
                    <button
                      type="button"
                      onClick={() => photoInputRef.current?.click()}
                      disabled={uploadingPhoto}
                      style={{
                        width: 70,
                        height: 70,
                        border: '1px dashed var(--border)',
                        borderRadius: 6,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--brand)',
                        background: '#fff',
                        cursor: uploadingPhoto ? 'wait' : 'pointer',
                        fontSize: 20,
                      }}
                      title="Add site photo"
                    >
                      <i
                        className={
                          uploadingPhoto
                            ? 'ph ph-spinner-gap'
                            : 'ph ph-plus'
                        }
                      />
                    </button>
                  ) : null}
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handlePickPhoto}
                    style={{ display: 'none' }}
                  />
                </div>
                {(dpr?.photoUris ?? []).length === 0 ? (
                  <div
                    style={{
                      fontSize: 11,
                      color: 'var(--text-tertiary)',
                      marginTop: 10,
                    }}
                  >
                    No photos yet for this day.
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ───── Small helpers ─────

function RightSection({
  title,
  right,
  children,
}: {
  title: string;
  right?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 8,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          background: '#F9FAFB',
          padding: '12px 16px',
          borderBottom: '1px solid var(--border)',
          fontSize: 13,
          fontWeight: 600,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <span>{title}</span>
        {right ? (
          <span style={{ fontWeight: 400, fontSize: 12 }}>{right}</span>
        ) : null}
      </div>
      {children}
    </div>
  );
}

// ───── Work-log feed row + previous-progress lookup ─────

function previousProgressForUpdate(
  row: ProjectTaskUpdateRow,
  all: ProjectTaskUpdateRow[],
  index: number,
): number {
  // The feed is sorted newest-first. The "previous" update for the
  // same task on this day sits AFTER `index` in the array (since
  // newer entries are earlier). Walk forward until we find another
  // update for the same task.
  for (let j = index + 1; j < all.length; j++) {
    if (all[j].taskId === row.taskId) return all[j].progress;
  }
  return 0;
}

function workLogInitial(name: string): string {
  const c = name.trim().charAt(0);
  return c ? c.toUpperCase() : '?';
}

// ───── Right-rail line rows ─────

const ATTENDANCE_TONE: Record<AttendanceStatus, { bg: string; fg: string; label: string }> = {
  present:    { bg: '#E3F5EB', fg: '#0F9D58', label: 'P' },
  half_day:   { bg: '#FEF3C7', fg: '#B45309', label: 'H' },
  absent:     { bg: '#FEE2E2', fg: '#B91C1C', label: 'A' },
  paid_leave: { bg: '#EDE9FE', fg: '#6D28D9', label: 'L' },
  week_off:   { bg: '#F1F5F9', fg: '#475569', label: 'W' },
};

function AttendanceLine({ row }: { row: AttendanceRecord }) {
  const tone = ATTENDANCE_TONE[row.status] ?? ATTENDANCE_TONE.absent;
  const hrs = hoursFor(row.status);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 14px',
        borderTop: '1px solid var(--border)',
      }}
    >
      <span
        style={{
          width: 22,
          height: 22,
          borderRadius: 6,
          background: tone.bg,
          color: tone.fg,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 11,
          fontWeight: 800,
          flexShrink: 0,
        }}
        title={row.status}
      >
        {tone.label}
      </span>
      <span
        style={{
          flex: 1,
          minWidth: 0,
          fontSize: 13,
          fontWeight: 600,
          color: 'var(--text-primary)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {row.labourName}
      </span>
      {typeof hrs === 'number' ? (
        <span
          style={{
            fontSize: 11,
            color: 'var(--text-tertiary)',
            fontFamily: 'Menlo, monospace',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {hrs.toFixed(1)}h
        </span>
      ) : null}
    </div>
  );
}

const MAT_TONE: Record<string, { bg: string; fg: string; label: string }> = {
  pending:  { bg: '#FEF3C7', fg: '#B45309', label: 'PENDING' },
  approved: { bg: '#E3F5EB', fg: '#0F9D58', label: 'APPROVED' },
  rejected: { bg: '#FEE2E2', fg: '#B91C1C', label: 'REJECTED' },
};

function MaterialRequestLine({ req }: { req: MaterialRequest }) {
  const tone = MAT_TONE[req.status] ?? MAT_TONE.pending;
  const itemCount = req.items?.length ?? 0;
  const previewItem = req.items?.[0]?.name;
  // Prefer the request's saved title; fall back to "first item + N
  // more" so a quick-add request without a title still reads
  // meaningfully in the list.
  const title =
    req.title?.trim() ||
    (previewItem
      ? `${previewItem}${itemCount > 1 ? ` + ${itemCount - 1} more` : ''}`
      : `Request ${req.id.slice(-6)}`);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 14px',
        borderTop: '1px solid var(--border)',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--text-primary)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontSize: 11,
            color: 'var(--text-tertiary)',
            marginTop: 2,
          }}
        >
          {itemCount} {itemCount === 1 ? 'item' : 'items'}
        </div>
      </div>
      <div
        style={{
          textAlign: 'right',
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 4,
        }}
      >
        <span
          style={{
            padding: '2px 8px',
            borderRadius: 999,
            background: tone.bg,
            color: tone.fg,
            fontSize: 9,
            fontWeight: 800,
            letterSpacing: 0.5,
          }}
        >
          {tone.label}
        </span>
        <span
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: 'var(--text-primary)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          ₹{(req.totalValue ?? 0).toLocaleString('en-IN')}
        </span>
      </div>
    </div>
  );
}

function WorkLogRow({
  row,
  previousProgress,
}: {
  row: ProjectTaskUpdateRow;
  previousProgress: number;
}) {
  const delta = row.progress - previousProgress;
  const time = relativeTime(row.createdAt?.toMillis?.() ?? null);
  return (
    <div
      style={{
        display: 'flex',
        gap: 12,
        padding: '14px 16px',
        borderTop: '1px solid var(--border)',
      }}
    >
      {/* Avatar */}
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          background: 'var(--brand-bg)',
          color: 'var(--brand)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 13,
          fontWeight: 700,
          flexShrink: 0,
        }}
      >
        {workLogInitial(row.authorName)}
      </div>

      {/* Body */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
          }}
        >
          <span
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--text-primary)',
            }}
          >
            {row.authorName}
          </span>
          <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
            {time}
          </span>
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              padding: '2px 6px',
              borderRadius: 4,
              background:
                delta > 0
                  ? 'var(--success-bg)'
                  : delta < 0
                    ? 'var(--critical-bg)'
                    : '#F3F4F6',
              color:
                delta > 0
                  ? 'var(--success-text)'
                  : delta < 0
                    ? 'var(--critical-text)'
                    : 'var(--text-secondary)',
              textTransform: 'uppercase',
              letterSpacing: '0.4px',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {delta > 0 ? `+${delta}%` : `${row.progress}%`}
          </span>
        </div>
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--text-secondary)',
            marginTop: 2,
          }}
        >
          {row.taskTitle}
        </div>
        {row.text ? (
          <div
            style={{
              fontSize: 12,
              color: 'var(--text-secondary)',
              lineHeight: 1.5,
              marginTop: 4,
              whiteSpace: 'pre-wrap',
            }}
          >
            {row.text}
          </div>
        ) : null}
        {row.photoUris.length > 0 ? (
          <div
            style={{
              display: 'flex',
              gap: 6,
              marginTop: 8,
              flexWrap: 'wrap',
            }}
          >
            {row.photoUris.slice(0, 3).map((url) => (
              <a
                key={url}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: 4,
                  overflow: 'hidden',
                  display: 'block',
                  background: '#E5E7EB',
                }}
              >
                <img
                  src={url}
                  alt="Work log"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
              </a>
            ))}
            {row.photoUris.length > 3 ? (
              <div
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: 4,
                  background: '#F5F5F5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 11,
                  color: 'var(--text-secondary)',
                  fontWeight: 600,
                }}
              >
                +{row.photoUris.length - 3}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
